"use client";

import { c as _c } from "react/compiler-runtime";
import { useVariableLabelResolver } from "@/app/(v2)/workflows/_components/VariableLabelContext";
import { MiddleEllipsis } from "@/components/ui/middle-ellipsis";
import { usePropValues } from "@/lib/contexts/PropValuesContext";
import { cn } from "@/lib/utils";
import React, { useCallback, useRef } from "react";
export const variablePillClassName = "inline-flex h-6 max-w-full items-center gap-0.5 truncate rounded-md bg-blue-100 px-2 align-middle text-xs font-medium leading-none text-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
export const variablePillAccentClassName = "text-blue-600 dark:text-blue-400";
export const inlineReferenceBadgeClassName = variablePillClassName;
export const inlineReferenceBadgeAccentClassName = variablePillAccentClassName;
export const propVariablePillClassName = "inline-flex h-6 max-w-full items-center gap-0.5 truncate rounded-md bg-violet-100 px-2 align-middle text-xs font-medium leading-none text-violet-700 dark:bg-violet-900/40 dark:text-violet-300";
export const propVariablePillAccentClassName = "text-violet-500 dark:text-violet-400";

/**
 * Compute a friendly display label for @ and $ variables.
 * Returns null for regular node variables (e.g., {{root.result.price}}).
 */
export function getFriendlyLabel(inner: string): string | null {
  if (inner.startsWith("@")) {
    const field = inner.slice(1);
    return field.split(/[_.]/).filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  }
  if (inner.startsWith("$")) {
    const body = inner.slice(1);
    const parts = body.split(".");
    if (parts[0] === "item") return parts.length === 1 ? "Current Item" : `Item: ${parts.slice(1).join(".")}`;
    if (parts[0] === "index") return "Current Index";
    if (parts[0] === "parent_item") return "Parent Item";
    if (parts[0] === "parent_index") return "Parent Index";
    if (parts[0] === "storage") return parts.length > 1 ? `Storage: ${parts.slice(1).join(".")}` : "Storage";
    if (parts[0] === "org") {
      if (parts[1] === "stores" && parts.length >= 4) return `Store ${parts[2]}: ${parts.slice(3).join(".")}`;
      if (parts[1] === "storage" && parts.length > 2) return `Org Storage: ${parts.slice(2).join(".")}`;
      return parts.length > 1 ? `Org: ${parts.slice(1).join(".")}` : "Org";
    }
    if (parts[0] === "for" && parts.length >= 3) return `For ${parts[1]}: ${parts.slice(2).join(".")}`;
    if (parts[0] === "props") return parts.length > 1 ? `Prop: ${parts.slice(1).join(".")}` : "Props";
    if (parts[0] === "workflowId") return "Workflow ID";
    return inner;
  }
  return null;
}

/**
 * Extract the prop key from a variable inner path like "$props.srcChainId".
 * Returns null if not a props variable.
 */
export function getPropKey(inner: string): string | null {
  if (!inner.startsWith("$props.")) return null;
  return inner.slice("$props.".length);
}

/**
 * Resolve a prop variable to its display value.
 * Returns null if the variable isn't a prop or has no value.
 */
function resolveDisplayValue(inner: string, propValues: Record<string, unknown> | undefined): string | null {
  if (!propValues) return null;
  const key = getPropKey(inner);
  if (!key) return null;
  const val = propValues[key];
  if (val === undefined || val === null || val === "") return null;
  return String(val);
}

/**
 * Extract the API node ID from a variable string like "{{root.result.text}}" → "root".
 * Returns null for special variables (@ and $).
 */
function getSourceApiNodeId(variable: string): string | null {
  const hasAtPrefix = variable.startsWith("@{{");
  const inner = hasAtPrefix ? variable.slice(3, -2) : variable.slice(2, -2);
  if (inner.startsWith("@") || inner.startsWith("$")) return null;
  const dotIndex = inner.indexOf(".");
  return dotIndex === -1 ? null : inner.slice(0, dotIndex);
}
const HIGHLIGHT_CLASS = "variable-source-highlight";

/**
 * VariablePill - Renders a template variable as a styled pill/badge
 * Compact version with truncation for use in action nodes.
 * For $props variables, shows the resolved value (when available)
 * with distinct purple styling to indicate it's still a variable.
 */
export function VariablePill(t0) {
  const $ = _c(64);
  const {
    variable,
    className
  } = t0;
  const propValues = usePropValues();
  const resolveLabel = useVariableLabelResolver();
  const highlightedRef = useRef(null);
  let t1;
  if ($[0] !== variable) {
    t1 = () => {
      const apiNodeId = getSourceApiNodeId(variable);
      if (!apiNodeId) {
        return;
      }
      const el = document.querySelector(`[data-api-node-id="${CSS.escape(apiNodeId)}"]`);
      if (el) {
        el.classList.add(HIGHLIGHT_CLASS);
        highlightedRef.current = el;
      }
    };
    $[0] = variable;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleMouseEnter = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (highlightedRef.current) {
        highlightedRef.current.classList.remove(HIGHLIGHT_CLASS);
        highlightedRef.current = null;
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleMouseLeave = t2;
  let t3;
  if ($[3] !== variable) {
    t3 = variable.startsWith("@{{");
    $[3] = variable;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const hasAtPrefix = t3;
  let hoverClass;
  let hoverProps;
  let innerPath;
  let t4;
  let t5;
  if ($[5] !== className || $[6] !== handleMouseEnter || $[7] !== hasAtPrefix || $[8] !== propValues || $[9] !== variable) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      innerPath = hasAtPrefix ? variable.slice(3, -2) : variable.slice(2, -2);
      const hasSourceNode = !!getSourceApiNodeId(variable);
      let t6;
      if ($[15] !== handleMouseEnter) {
        t6 = {
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave
        };
        $[15] = handleMouseEnter;
        $[16] = t6;
      } else {
        t6 = $[16];
      }
      hoverProps = t6;
      hoverClass = hasSourceNode ? "variable-pill-hover" : undefined;
      const resolvedValue = resolveDisplayValue(innerPath, propValues);
      if (resolvedValue !== null) {
        const propKey = getPropKey(innerPath);
        let t7;
        if ($[17] !== className || $[18] !== hoverClass) {
          t7 = cn(propVariablePillClassName, hoverClass, className);
          $[17] = className;
          $[18] = hoverClass;
          $[19] = t7;
        } else {
          t7 = $[19];
        }
        const t8 = `Prop: ${propKey} = ${resolvedValue}`;
        let t9;
        if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = cn("truncate", propVariablePillAccentClassName);
          $[20] = t9;
        } else {
          t9 = $[20];
        }
        let t10;
        if ($[21] !== propKey) {
          t10 = <span className={t9}>{propKey}:</span>;
          $[21] = propKey;
          $[22] = t10;
        } else {
          t10 = $[22];
        }
        let t11;
        if ($[23] !== resolvedValue) {
          t11 = <span className="truncate">{resolvedValue}</span>;
          $[23] = resolvedValue;
          $[24] = t11;
        } else {
          t11 = $[24];
        }
        let t12;
        if ($[25] !== hoverProps || $[26] !== t10 || $[27] !== t11 || $[28] !== t7 || $[29] !== t8) {
          t12 = <span className={t7} title={t8} {...hoverProps}>{t10}{t11}</span>;
          $[25] = hoverProps;
          $[26] = t10;
          $[27] = t11;
          $[28] = t7;
          $[29] = t8;
          $[30] = t12;
        } else {
          t12 = $[30];
        }
        t5 = t12;
        break bb0;
      }
      t4 = getFriendlyLabel(innerPath);
    }
    $[5] = className;
    $[6] = handleMouseEnter;
    $[7] = hasAtPrefix;
    $[8] = propValues;
    $[9] = variable;
    $[10] = hoverClass;
    $[11] = hoverProps;
    $[12] = innerPath;
    $[13] = t4;
    $[14] = t5;
  } else {
    hoverClass = $[10];
    hoverProps = $[11];
    innerPath = $[12];
    t4 = $[13];
    t5 = $[14];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  const friendly = t4;
  if (friendly) {
    let t6;
    if ($[31] !== className || $[32] !== hoverClass) {
      t6 = cn(variablePillClassName, hoverClass, className);
      $[31] = className;
      $[32] = hoverClass;
      $[33] = t6;
    } else {
      t6 = $[33];
    }
    let t7;
    if ($[34] !== friendly) {
      t7 = <span className="truncate">{friendly}</span>;
      $[34] = friendly;
      $[35] = t7;
    } else {
      t7 = $[35];
    }
    let t8;
    if ($[36] !== hoverProps || $[37] !== innerPath || $[38] !== t6 || $[39] !== t7) {
      t8 = <span className={t6} title={innerPath} {...hoverProps}>{t7}</span>;
      $[36] = hoverProps;
      $[37] = innerPath;
      $[38] = t6;
      $[39] = t7;
      $[40] = t8;
    } else {
      t8 = $[40];
    }
    return t8;
  }
  let t6;
  if ($[41] !== resolveLabel || $[42] !== variable) {
    t6 = resolveLabel(variable);
    $[41] = resolveLabel;
    $[42] = variable;
    $[43] = t6;
  } else {
    t6 = $[43];
  }
  const displayLabel = t6;
  if (displayLabel) {
    let t7;
    if ($[44] !== className || $[45] !== hoverClass) {
      t7 = cn(variablePillClassName, hoverClass, className);
      $[44] = className;
      $[45] = hoverClass;
      $[46] = t7;
    } else {
      t7 = $[46];
    }
    let t8;
    if ($[47] !== displayLabel) {
      t8 = <span className="truncate">{displayLabel}</span>;
      $[47] = displayLabel;
      $[48] = t8;
    } else {
      t8 = $[48];
    }
    let t9;
    if ($[49] !== hoverProps || $[50] !== innerPath || $[51] !== t7 || $[52] !== t8) {
      t9 = <span className={t7} title={innerPath} {...hoverProps}>{t8}</span>;
      $[49] = hoverProps;
      $[50] = innerPath;
      $[51] = t7;
      $[52] = t8;
      $[53] = t9;
    } else {
      t9 = $[53];
    }
    return t9;
  }
  const atPrefix = hasAtPrefix ? "@" : "";
  let t7;
  if ($[54] !== className || $[55] !== hoverClass) {
    t7 = cn(variablePillClassName, hoverClass, className);
    $[54] = className;
    $[55] = hoverClass;
    $[56] = t7;
  } else {
    t7 = $[56];
  }
  const t8 = `${atPrefix}${innerPath}`;
  let t9;
  if ($[57] !== t8) {
    t9 = <MiddleEllipsis text={t8} />;
    $[57] = t8;
    $[58] = t9;
  } else {
    t9 = $[58];
  }
  let t10;
  if ($[59] !== hoverProps || $[60] !== innerPath || $[61] !== t7 || $[62] !== t9) {
    t10 = <span className={t7} title={innerPath} {...hoverProps}>{t9}</span>;
    $[59] = hoverProps;
    $[60] = innerPath;
    $[61] = t7;
    $[62] = t9;
    $[63] = t10;
  } else {
    t10 = $[63];
  }
  return t10;
}

/**
 * Check if a string value contains template variables
 */
export function containsTemplateVariable(value: unknown): boolean {
  if (typeof value !== "string") return false;
  return /@?\{\{[^}]+\}\}/.test(value);
}

/**
 * Renders a value that may contain template variables as styled pills
 * Returns the original value if it's not a string or doesn't contain variables
 */
export function renderValueWithVariables(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-ds-text-disabled">null</span>;
  }
  const stringValue = String(value);

  // Check if the entire value is a variable (with or without @ prefix)
  if (/^@?\{\{[^}]+\}\}$/.test(stringValue)) {
    return <VariablePill variable={stringValue} />;
  }

  // Check if the value contains any variables
  const parts = stringValue.split(/(@?\{\{[^}]+\}\})/g);
  if (parts.length === 1) {
    // No variables found, render as plain text
    return <span>{stringValue}</span>;
  }

  // Mix of text and variables
  return <>
      {parts.map((part, i) => {
      if (/^@?\{\{[^}]+\}\}$/.test(part)) {
        return <VariablePill key={i} variable={part} />;
      }
      return part ? <span key={i}>{part}</span> : null;
    })}
    </>;
}